import { CustomerSimplifiedFormData } from '@/components/Customers/CustomerSimplifiedForm';
import { WorkorderImportData } from '@/components/Movements/WorkOrders/WorkorderImport/WorkorderImportModal';
import { VehicleFormValues } from '@/components/_common/_VehicleModal/VehicleModal.types';
import { getAccessoryId } from '@/components/_common/_VehicleModal/getAccessoryId';
import { useEmployeeIsEstimatorMutate } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { useCustomerAddressesMutation } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import { CustomerAddress } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.types';
import { useCustomerContactsMutation } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';

import {
  useCustomerGeneralMutation,
  useCustomerReducedList,
} from '@/core/domain/people/Customers/Customers.hooks';
import {
  CustomerGeneral,
  PersonType,
} from '@/core/domain/people/Customers/Customers.types';
import { Contact } from '@/core/domain/people/_shared/Contact';
import { CustomerType } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { WorkOrderImportGeneralFormValues } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import flatContacts from '@/utils/functions/flatContacts';
import { removeMask, applyCpfCnpjMask, applyMask } from '@/utils/mask';
import { phoneMask, cepMask, licensePlateMask } from '@/utils/mask/commonMasks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { getUserFromLocalStorage } from '@/utils/user';
import { Form } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';

export function useImportDataToForms() {
  const [importData, setImportData] = useState<WorkorderImportData>();

  const [generalForm] = Form.useForm<WorkOrderImportGeneralFormValues>();
  const [customerForm] = Form.useForm<CustomerSimplifiedFormData>();
  const [vehicleForm] = Form.useForm<VehicleFormValues>();

  const customerFormValues = Form.useWatch([], customerForm);

  const localStorage = getUserFromLocalStorage();
  const employeeId = Number(localStorage.employeeId);

  const employeeIsEstimator = useEmployeeIsEstimatorMutate();
  const customerGeneralMutate = useCustomerGeneralMutation();
  const customerAddressMutate = useCustomerAddressesMutation();
  const customerContactMutate = useCustomerContactsMutation();

  const customerList = useCustomerReducedList(
    { cpfCnpj: removeMask(customerFormValues?.cpfCnpj), rows: 1 },
    !customerAddressMutate.data && !!customerFormValues?.cpfCnpj,
  );
  const customersData = flatInfiniteData(customerList.data);

  useEffect(() => {
    (async () => {
      if (customersData.length && !customerFormValues.address) {
        const customerId = customersData[0].id;
        const addresses = await customerAddressMutate.mutateAsync(customerId);

        customerForm.setFieldValue('address', addresses.data);
      }
    })();
  }, [customerList.dataUpdatedAt]);

  useEffect(() => {
    (async () => {
      if (importData) {
        const { customer, vehicle, items, ...restData } = importData.data;

        const employee = await employeeIsEstimator.mutateAsync(employeeId);

        generalForm.setFieldsValue({
          ...restData,
          estimatorId: employee.isEstimator ? employeeId : undefined,
          insurerId: restData.insurer?.id,
          insurerName: restData.insurer?.name,
          customerType:
            !!restData?.insurer ||
            !!restData?.accidentNumber ||
            !!restData?.deductibleValue
              ? CustomerType.Insurance
              : CustomerType.Private,
          items: items.map((item) => ({
            ...item,
            discount: (item.discount || 0) / 100,
            isInternal: !item.isInternal,
            isInternalDisabled: item.isInternal,
            id: crypto.randomUUID(),
          })),
          origin: importData.origin,
          accidentNumber: restData?.accidentNumber,
          ciliaBudgetNumber: restData?.budgetNumber,
          insurerDeductibleAmount: restData.deductibleValue,
          entryDate: moment(),
        });

        if (customer) {
          let customerAddressMain: CustomerAddress | null = null;
          let customerAddress: CustomerAddress | null = null;
          let customerGeneral: CustomerGeneral | undefined;
          let customerContact: Contact[] | undefined;

          if (customer.id) {
            const addresses = await customerAddressMutate.mutateAsync(
              customer.id,
            );

            if (addresses.data?.length) {
              customerAddressMain =
                addresses.data.find((address) => address.main) || null;
              customerAddress = addresses.data?.at(0) || null;
            }

            customerGeneral = await customerGeneralMutate.mutateAsync(
              customer.id,
            );

            if (!customer.phoneNumber || !customer.cellPhone) {
              customerContact = await customerContactMutate.mutateAsync(
                customer.id,
              );
            }
          }

          const address =
            customerAddressMain || customer.address || customerAddress;

          const phoneNumber = flatContacts(customerContact)?.find(
            (contact) => contact.contactType === 'PHONE',
          )?.value;

          const cellNumber = flatContacts(customerContact)?.find(
            (contact) => contact.contactType === 'MOBILE',
          )?.value;

          const phone = phoneNumber || cellNumber;

          const email = flatContacts(customerContact)?.find(
            (contact) => contact.contactType === 'EMAIL',
          )?.value;

          customerForm.setFieldsValue({
            ...customer,
            ...customerGeneral,
            birthDate: !!customerGeneral?.birthDate
              ? moment(customerGeneral?.birthDate)
              : undefined,
            recommendation: !!customerGeneral?.recommendationId
              ? { id: customerGeneral?.recommendationId }
              : undefined,
            cpfCnpj: customer.documentNumber
              ? applyCpfCnpjMask(removeMask(customer.documentNumber))
              : '',
            personType:
              customer.customerType && customer.customerType === 'F'
                ? PersonType.Natural
                : customer.customerType && customer.customerType === 'J'
                ? PersonType.Legal
                : undefined,
            phone: customer.phoneNumber?.length
              ? applyMask(removeMask(customer.phoneNumber), phoneMask)
              : !!phone
              ? applyMask(removeMask(phoneNumber), phoneMask)
              : '',
            email: email || '',
            name: customer.name || '',
            address: !!address
              ? {
                  ...address,
                  zipCode: applyMask(removeMask(address.zipCode), cepMask),
                }
              : undefined,
          });
        }

        if (vehicle) {
          vehicleForm.setFieldsValue({
            ...vehicle,
            chassi: vehicle.chassis,
            licensePlate: applyMask(vehicle.licensePlate, licensePlateMask),
            yearModel: String(vehicle.modelYear),
            yearFabrication: String(vehicle.manufactureYear),
            km: vehicle.averageMileagePerMonth,
            accessories: vehicle.accessories?.length
              ? vehicle.accessories?.map((item) => getAccessoryId(item))
              : [],
          });
        }
      }
    })();
  }, [importData]);

  return {
    importData,
    setImportData,
    forms: {
      generalForm,
      customerForm,
      vehicleForm,
    },
  };
}
